import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { PostInterviewStep } from "../../../../redux/reducers/assessmentReducer";

interface PostInterviewStepsEditorProps {
  steps: PostInterviewStep[];
  onChange: (steps: PostInterviewStep[]) => void;
  isEditing: boolean;
}

const PostInterviewStepsEditor: React.FC<PostInterviewStepsEditorProps> = ({
  steps,
  onChange,
  isEditing,
}) => {
  const [expandedStep, setExpandedStep] = useState<number | false>(false);

  const handleStepChange = (
    index: number,
    field: keyof PostInterviewStep,
    value: any
  ) => {
    const newSteps = [...steps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: value,
    };
    onChange(newSteps);
  };

  const handleAddStep = () => {
    const newStep: PostInterviewStep = {
      min_score:
        steps.length > 0 ? Math.max(...steps.map((s) => s.min_score)) + 1 : 0,
      outro_text: "",
      tone: "warm",
      share_summary: true,
      ask_questions_at_end: true,
      will_end_chat: true,
      qa_context:
        "{{bot_name}} is answering {{human_name}}'s questions after an interview, answering questions about ",
      qa_next_item_on_agenda: "ending the call",
      qa_additional_topics: ["next steps"],
      qa_post:
        "{{bot_name}} thanks {{last_human_name}} and says they'll be in touch very soon.",
    };
    onChange([...steps, newStep]);
    setExpandedStep(steps.length);
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    onChange(newSteps);
  };

  const handleDuplicateStep = (index: number) => {
    const newSteps = [...steps];
    const duplicatedStep = {
      ...steps[index],
      min_score: steps[index].min_score + 0.5,
    };
    newSteps.splice(index + 1, 0, duplicatedStep);
    onChange(newSteps);
  };

  const handleTopicChange = (
    stepIndex: number,
    topicIndex: number,
    value: string
  ) => {
    const newSteps = [...steps];
    const topics = [...(newSteps[stepIndex].qa_additional_topics || [])];
    topics[topicIndex] = value;
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      qa_additional_topics: topics,
    };
    onChange(newSteps);
  };

  const handleAddTopic = (stepIndex: number) => {
    const newSteps = [...steps];
    const topics = [...(newSteps[stepIndex].qa_additional_topics || [])];
    topics.push("");
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      qa_additional_topics: topics,
    };
    onChange(newSteps);
  };

  const handleRemoveTopic = (stepIndex: number, topicIndex: number) => {
    const newSteps = [...steps];
    const topics = [...(newSteps[stepIndex].qa_additional_topics || [])];
    topics.splice(topicIndex, 1);
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      qa_additional_topics: topics,
    };
    onChange(newSteps);
  };

  const getScoreRangeLabel = (step: PostInterviewStep, index: number) => {
    const minScore = step.min_score || 0;
    const sortedSteps = [...steps].sort((a, b) => a.min_score - b.min_score);
    const stepIndex = sortedSteps.findIndex((s) => s === step);
    const nextStep = sortedSteps[stepIndex + 1];
    const maxScore = nextStep ? nextStep.min_score - 0.01 : 5;

    if (minScore === maxScore) {
      return `Score ${minScore}`;
    }
    return `Score ≥ ${minScore}`;
  };

  // Available VoiceEmotion values from backend (from server/voice_enums.py)
  const availableTones = [
    // V1 emotions
    "neutral",
    "calm",
    "concerned",
    "confused",
    "empathetic",
    "excited",
    "interested",
    "happy",
    "sad",
    "angry",
    "contempt",
    "fearful",
    "disgusted",
    "surprised",
    // V2 emotions
    "admiration",
    "adoration",
    "appreciation",
    "amused",
    "anxious",
    "awe",
    "awkward",
    "bored",
    "craving",
    "entranced",
    "horror",
    "nostalgic",
    "relieved",
    "romantic",
    "satisfied",
    "sarcastic",
    "soft",
    "laughing",
    // V3 emotions
    "appraising",
    "bragging",
    "casual",
    "cautious",
    "confident",
    "cutting",
    "demon",
    "dismissive",
    "dry",
    "engaging",
    "furious",
    "intense",
    "kill_sequence_initiate",
    "lecturing",
    "meditative",
    "mocking",
    "motivating",
    "mysterious",
    "optimistic",
    "passionate",
    "persuasive",
    "pessimistic",
    "playful",
    "plead",
    "plead_angry",
    "plead_desperate",
    "positive",
    "professional",
    "proud",
    "redirecting",
    "relief",
    "storytelling",
    "urgent",
    "warm",
    "whispering",
    "robot",
    "garbled",
    // Synthetic emotions
    "steady",
    "irritated",
    "frustrated",
    "agitated",
    "resigned",
    "snarky",
    "haughty",
  ];

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6">Post-Interview Steps (Outros)</Typography>
            <Typography variant="body2" color="text.secondary">
              Define different outro messages and Q&A settings based on
              interview scores
            </Typography>
          </Box>
          {isEditing && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              size="small"
              variant="outlined"
            >
              Add Step
            </Button>
          )}
        </Box>

        {steps.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            No post-interview steps defined. Add steps to customize the outro
            experience based on scores.
          </Typography>
        ) : (
          [...steps]
            .sort((a, b) => a.min_score - b.min_score)
            .map((step, index) => (
              <Accordion
                key={index}
                expanded={expandedStep === index}
                onChange={(_, isExpanded) =>
                  setExpandedStep(isExpanded ? index : false)
                }
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "grey.50",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Chip
                      label={getScoreRangeLabel(step, index)}
                      size="small"
                      color="primary"
                      sx={{ minWidth: 100 }}
                    />
                    <Chip
                      label={step.tone || "warm"}
                      size="small"
                      variant="outlined"
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {step.outro_text
                        ? step.outro_text.length > 60
                          ? `${step.outro_text.substring(0, 60)}...`
                          : step.outro_text
                        : "No outro text defined"}
                    </Typography>
                    {isEditing && (
                      <Box
                        display="flex"
                        gap={1}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicateStep(index)}
                          title="Duplicate step"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveStep(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Basic Settings */}
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Minimum Score"
                        type="number"
                        value={step.min_score}
                        onChange={(e) =>
                          handleStepChange(
                            index,
                            "min_score",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        disabled={!isEditing}
                        helperText="Min score for this outro"
                        inputProps={{ min: 0, max: 5, step: 0.1 }}
                        sx={{ width: 200 }}
                      />
                      <FormControl sx={{ width: 200 }} disabled={!isEditing}>
                        <InputLabel>Tone</InputLabel>
                        <Select
                          value={step.tone || "warm"}
                          onChange={(e) =>
                            handleStepChange(index, "tone", e.target.value)
                          }
                          label="Tone"
                        >
                          {availableTones.map((tone) => (
                            <MenuItem key={tone} value={tone}>
                              {tone.charAt(0).toUpperCase() + tone.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Outro Text */}
                    <TextField
                      label="Outro Message"
                      fullWidth
                      multiline
                      rows={3}
                      value={step.outro_text || ""}
                      onChange={(e) =>
                        handleStepChange(index, "outro_text", e.target.value)
                      }
                      disabled={!isEditing}
                      helperText="The message to display at the end of the interview. You can use {{bot_name}} and {{human_name}} as placeholders."
                    />

                    {/* Outro Settings */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Outro Settings
                      </Typography>
                      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={step.share_summary !== false}
                              onChange={(e) =>
                                handleStepChange(
                                  index,
                                  "share_summary",
                                  e.target.checked
                                )
                              }
                              disabled={!isEditing}
                            />
                          }
                          label="Share Summary"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={step.ask_questions_at_end !== false}
                              onChange={(e) =>
                                handleStepChange(
                                  index,
                                  "ask_questions_at_end",
                                  e.target.checked
                                )
                              }
                              disabled={!isEditing}
                            />
                          }
                          label="Ask Questions at End"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={step.will_end_chat !== false}
                              onChange={(e) =>
                                handleStepChange(
                                  index,
                                  "will_end_chat",
                                  e.target.checked
                                )
                              }
                              disabled={!isEditing}
                            />
                          }
                          label="Will End Chat"
                        />
                      </Box>
                    </Box>

                    <Divider />

                    {/* Q&A Configuration */}
                    <Typography variant="subtitle2">
                      Q&A Session Configuration
                    </Typography>

                    <TextField
                      label="Q&A Context"
                      fullWidth
                      multiline
                      rows={2}
                      value={step.qa_context || ""}
                      onChange={(e) =>
                        handleStepChange(index, "qa_context", e.target.value)
                      }
                      disabled={!isEditing}
                      helperText="Context for the Q&A session. Use {{bot_name}} and {{human_name}} as placeholders."
                    />

                    <TextField
                      label="Next Item on Agenda"
                      fullWidth
                      value={step.qa_next_item_on_agenda || ""}
                      onChange={(e) =>
                        handleStepChange(
                          index,
                          "qa_next_item_on_agenda",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      helperText="What comes after the Q&A session"
                    />

                    <TextField
                      label="Post Q&A Message"
                      fullWidth
                      multiline
                      rows={2}
                      value={step.qa_post || ""}
                      onChange={(e) =>
                        handleStepChange(index, "qa_post", e.target.value)
                      }
                      disabled={!isEditing}
                      helperText="Message after Q&A ends. Use {{bot_name}} and {{last_human_name}} as placeholders."
                    />

                    {/* Q&A Topics */}
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="subtitle2">
                          Additional Q&A Topics
                        </Typography>
                        {isEditing && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddTopic(index)}
                          >
                            Add Topic
                          </Button>
                        )}
                      </Box>
                      {step.qa_additional_topics &&
                      step.qa_additional_topics.length > 0 ? (
                        step.qa_additional_topics.map((topic, topicIndex) => (
                          <Box key={topicIndex} display="flex" gap={1} mb={1}>
                            <TextField
                              fullWidth
                              size="small"
                              value={topic}
                              onChange={(e) =>
                                handleTopicChange(
                                  index,
                                  topicIndex,
                                  e.target.value
                                )
                              }
                              disabled={!isEditing}
                              placeholder={`Topic ${topicIndex + 1}`}
                            />
                            {isEditing && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRemoveTopic(index, topicIndex)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No additional topics defined
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
        )}
      </CardContent>
    </Card>
  );
};

export default PostInterviewStepsEditor;
